import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Divider,
  Collapse,
} from "@mui/material";
import { useState } from "react";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#449095" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#449095" },
};

const FORMATS   = ["CEF", "LEEF", "JSON", "RAW", "RFC 5424"];
const PROTOCOLS = ["UDP", "TCP"];

const INITIAL = {
  host:            "",
  port:            "514",
  format:          "CEF",
  protocol:        "UDP",
  hostname:        "",
  tls:             false,
  tls_ca_cert:     "",
  verify_hostname: false,
};

const ServerSyslogConfig = () => {
  const [cfg, setCfg] = useState(INITIAL);
  const set = (key, val) => setCfg((prev) => ({ ...prev, [key]: val }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

      {/* ── Header ────────────────────────────────────────────── */}
      <Box>
        <Typography sx={{ fontWeight: 600, color: "#e8e8e8" }}>Cloud Syslog</Typography>
        <Typography variant="caption" color="text.secondary">
          Configure the destination syslog server where the platform will forward
          processed events. Data flows from LECS to the cloud, then out to your syslog target.
        </Typography>
      </Box>

      <Divider />

      {/* ── Destination ───────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          Destination
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            size="small" label="Host" required
            value={cfg.host} onChange={(e) => set("host", e.target.value)}
            sx={{ flex: 1, minWidth: 160 }}
            placeholder="syslog.example.com"
          />
          <TextField
            size="small" label="Port"
            value={cfg.port} onChange={(e) => set("port", e.target.value)}
            sx={{ minWidth: 100 }}
          />
          <TextField
            select size="small" label="Protocol"
            value={cfg.protocol} onChange={(e) => set("protocol", e.target.value)}
            sx={{ minWidth: 120 }}
          >
            {PROTOCOLS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Format"
            value={cfg.format} onChange={(e) => set("format", e.target.value)}
            sx={{ minWidth: 140 }}
          >
            {FORMATS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </TextField>
        </Stack>
        <TextField
          size="small" label="Hostname (optional)"
          value={cfg.hostname} onChange={(e) => set("hostname", e.target.value)}
          placeholder="Leave empty to use platform hostname"
          helperText="Custom hostname included in syslog messages"
          sx={{ mt: 1.5, maxWidth: 360 }}
          fullWidth
        />
      </Box>

      {/* ── TLS ───────────────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          TLS (TCP only)
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={cfg.tls}
              onChange={(e) => { set("tls", e.target.checked); if (!e.target.checked) set("tls_ca_cert", ""); }}
              sx={switchSx}
            />
          }
          label="Enable TLS"
        />
        <Collapse in={cfg.tls} timeout={300}>
          <Box sx={{
            mt: 1.5, p: 2, display: "flex", flexDirection: "column", gap: 2,
            backgroundColor: "rgba(69,144,149,0.05)", borderRadius: 2,
            border: "1px solid rgba(69,144,149,0.2)",
          }}>
            <TextField
              size="small" label="CA Certificate" multiline rows={8}
              value={cfg.tls_ca_cert} onChange={(e) => set("tls_ca_cert", e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----"
              helperText="PEM-encoded CA certificate (optional)"
              sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.875rem" } }}
            />
            <FormControlLabel
              control={
                <Switch checked={cfg.verify_hostname} onChange={(e) => set("verify_hostname", e.target.checked)} sx={switchSx} />
              }
              label="Verify Hostname"
            />
          </Box>
        </Collapse>
      </Box>

    </Box>
  );
};

export default ServerSyslogConfig;
